import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { Button, Cell, Popover, TodoMark, Tooltip } from '@highlight/ui'
import { Clock, Plus, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { useReminderDraft, useReminderIsMultiline, useReminderCurrentMatch, useReminderDraftScheduledAt } from './atoms'
import { type ReminderItemType } from '../../hooks/use-reminders'
import { useUploadFile } from '@renderer/chat/hooks'
import { imageFilesFromClipboard, isImageFile, uploadImageFile } from './utils/image-attachments'
import { useTimeExpressionHighlight } from './hooks/use-time-expression-highlight'
import { parseTimeExpression } from './utils/parse-time-expression'
import { formatScheduledAt } from './utils/format-scheduled-at'
import { AnimatedTimeLabel } from './animated-time-label'
import { ReminderThumbnail } from './reminders-inline-image-preview'
import { ReminderDatePicker } from './date-picker'
import { SIDEPANEL_CELL_SURFACE, TODO_MARK, TODO_TITLE_TYPE } from '../cells/surface'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { formatUserTime, usePreferredHourCycle } from './utils/user-time-format'

const WRAP_RATIO = 0.7
const ACTIONS_HEIGHT = 32

interface PendingAttachment {
  localId: string
  name: string
  previewUrl: string
  fileId?: string
  uploading: boolean
}

interface ReminderInputProps {
  onSubmit: (
    value: string,
    type: ReminderItemType,
    scheduledAt?: Date,
    attachmentFileIds?: Array<string>,
    meta?: { timeExpressionUsed?: boolean },
  ) => void
  /** `todo` draws it as a blank row in the To-dos list: an empty mark, and text that comes up on hover. */
  appearance?: 'composer' | 'todo'
}

export const ReminderInput = memo(function ReminderInput({ onSubmit, appearance = 'composer' }: ReminderInputProps) {
  const isTodo = appearance === 'todo'
  const inputRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isMultiline, setIsMultiline] = useReminderIsMultiline()
  const [storedMatch, setStoredMatch] = useReminderCurrentMatch()
  const [hasContent, setHasContent] = useState(false)
  const [draft, setDraft] = useReminderDraft()
  const [pendingAttachments, setPendingAttachments] = useState<Array<PendingAttachment>>([])
  const [draftScheduledAt, setDraftScheduledAt] = useReminderDraftScheduledAt()
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const pendingAttachmentsRef = useRef(pendingAttachments)
  pendingAttachmentsRef.current = pendingAttachments

  const uploadFile = useUploadFile()
  const { handleHighlightInput, dismissMatch, hasMatch, currentMatch } = useTimeExpressionHighlight(inputRef)
  const [showTimeSuggestion, setShowTimeSuggestion] = useState(false)
  const timeSuggestionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hourCycle = usePreferredHourCycle()

  useEffect(() => {
    return () => {
      for (const p of pendingAttachmentsRef.current) {
        if (p.previewUrl.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(p.previewUrl)
          } catch {
            /* ignore */
          }
        }
      }
    }
  }, [])

  useEffect(() => {
    if (timeSuggestionTimer.current) {
      clearTimeout(timeSuggestionTimer.current)
      timeSuggestionTimer.current = null
    }
    if (hasMatch) {
      timeSuggestionTimer.current = setTimeout(() => setShowTimeSuggestion(true), 500)
    } else {
      timeSuggestionTimer.current = setTimeout(() => setShowTimeSuggestion(false), 250)
    }
  }, [hasMatch])

  useEffect(() => {
    if (currentMatch) {
      setStoredMatch({
        text: currentMatch.text,
        startIndex: currentMatch.startIndex,
        endIndex: currentMatch.endIndex,
        date: currentMatch.date.toISOString(),
      })
    } else {
      setStoredMatch(null)
    }
  }, [currentMatch, setStoredMatch])

  useEffect(() => {
    if (!draft?.trim() || !inputRef.current) return
    inputRef.current.textContent = draft
    setHasContent(true)
    if (storedMatch) {
      setShowTimeSuggestion(true)
    }
    measureLayout()
    handleHighlightInput()
    setTimeout(() => measureLayout(), 300)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getTextContent = useCallback(() => {
    return inputRef.current?.innerText ?? ''
  }, [])

  const measureLayout = useCallback(() => {
    const el = inputRef.current
    const container = containerRef.current
    if (!el || !container) return

    const text = el.textContent ?? ''

    if (!text.trim()) {
      setIsMultiline(false)
      return
    }

    const hasLineBreak = el.querySelector('br') !== null || text.includes('\n')
    if (hasLineBreak) {
      setIsMultiline(true)
      return
    }

    // When a <mark> tag is present (time expression highlight), the rendered width
    // is wider than plain text due to mark padding/styling. Use scrollWidth as a
    // reliable measure of the actual rendered content width.
    const hasMark = el.querySelector('mark') !== null
    if (hasMark) {
      setIsMultiline(true)
      return
    }

    const ctx = document.createElement('canvas').getContext('2d')
    if (ctx) {
      const style = getComputedStyle(el)
      ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
      const textWidth = ctx.measureText(text).width
      const containerWidth = container.offsetWidth
      const wraps = textWidth > containerWidth * WRAP_RATIO
      setIsMultiline(wraps)
    }
  }, [setIsMultiline])

  const removePendingAttachment = useCallback((localId: string) => {
    setPendingAttachments((current) => {
      const found = current.find((a) => a.localId === localId)
      if (found?.previewUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(found.previewUrl)
        } catch {
          /* ignore */
        }
      }
      return current.filter((a) => a.localId !== localId)
    })
  }, [])

  const handleFilesSelected = useCallback(
    async (files: Iterable<File> | null) => {
      for (const file of files ?? []) {
        if (!isImageFile(file)) continue

        const localId = crypto.randomUUID()
        const previewUrl = URL.createObjectURL(file)

        setPendingAttachments((prev) => [...prev, { localId, name: file.name, previewUrl, uploading: true }])

        try {
          const fileId = await uploadImageFile(uploadFile, file)
          setPendingAttachments((prev) =>
            prev.map((a) => (a.localId === localId ? { ...a, fileId, uploading: false } : a)),
          )
        } catch (error) {
          Sentry.captureException(error)
          setPendingAttachments((prev) => prev.filter((a) => a.localId !== localId))
          if (previewUrl.startsWith('blob:')) {
            try {
              URL.revokeObjectURL(previewUrl)
            } catch {
              /* ignore */
            }
          }
        }
      }
    },
    [uploadFile],
  )

  const manualDate = draftScheduledAt ? new Date(draftScheduledAt) : null

  const submit = (forceTask?: boolean) => {
    const rawValue = getTextContent().trim()
    if (!rawValue && pendingAttachments.length === 0) return

    const match = manualDate ? null : (currentMatch ?? (hasMatch === false ? null : parseTimeExpression(rawValue)))
    const scheduledAt = manualDate ?? match?.date
    const type: ReminderItemType = forceTask || scheduledAt ? 'task' : 'note'

    const title = match
      ? (rawValue.slice(0, match.startIndex) + rawValue.slice(match.endIndex)).replace(/ {2,}/g, ' ').trim()
      : rawValue

    const resolvedTitle = title || rawValue

    const fileIds = pendingAttachments.filter((a) => a.fileId && !a.uploading).map((a) => a.fileId!)

    const timeExpressionUsed = !!match && !manualDate
    onSubmit(resolvedTitle, type, scheduledAt, fileIds.length > 0 ? fileIds : undefined, { timeExpressionUsed })

    if (inputRef.current) {
      inputRef.current.textContent = ''
    }
    setHasContent(false)
    setDraft('')
    setIsMultiline(false)
    setStoredMatch(null)
    setShowTimeSuggestion(false)
    setDraftScheduledAt(null)
    setPendingAttachments([])
    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }

  const handleInput = useCallback(() => {
    const text = inputRef.current?.innerText ?? ''

    const trimmed = text.trim()
    setHasContent(trimmed.length > 0)
    setDraft(trimmed.length > 0 ? text : '')
    measureLayout()
    handleHighlightInput()
    setTimeout(() => measureLayout(), 300)
  }, [measureLayout, handleHighlightInput])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace' && showTimeSuggestion && storedMatch) {
      const el = inputRef.current
      const selection = window.getSelection()
      if (el && selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        const preRange = document.createRange()
        preRange.selectNodeContents(el)
        preRange.setEnd(range.startContainer, range.startOffset)
        const caretOffset = preRange.toString().length
        if (caretOffset === storedMatch.endIndex) {
          event.preventDefault()
          dismissMatch()
          measureLayout()
          return
        }
      }
    }

    if (event.key === 'Enter' && !event.shiftKey && !event.metaKey) {
      event.preventDefault()
      submit()
    }

    if (event.key === 'Enter' && event.metaKey) {
      event.preventDefault()
      submit(true)
    }
  }

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      const imageFiles = imageFilesFromClipboard(event.clipboardData)
      if (imageFiles.length === 0) return

      event.preventDefault()
      event.stopPropagation()
      void handleFilesSelected(imageFiles)
    },
    [handleFilesSelected],
  )

  const handleBlur = (event: FocusEvent) => {
    if (event.currentTarget.contains(event.relatedTarget)) return
    setIsFocused(false)
    const text = inputRef.current?.innerText ?? ''
    setHasContent(text.trim().length > 0)
  }

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const onFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await handleFilesSelected(e.target.files)
    e.target.value = ''
  }

  const showFooterChrome = hasContent || showTimeSuggestion || manualDate !== null || pendingAttachments.length > 0

  return (
    <Cell
      size="compact"
      // Deliberately no `focused` — the Cell's blue outline is for rows you land
      // on and act on, and here the caret already says where you are. The
      // container below carries the focus state instead.
      onClick={() => inputRef.current?.focus()}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={handleBlur}
      className={twMerge(
        'gap-2 px-3',
        isTodo ? 'py-1.5' : 'py-2',
        SIDEPANEL_CELL_SURFACE,
        // A to-do's hover only brings its text up, with no container. `after:hidden`
        // drops the Cell's own hover lift.
        isTodo && 'after:hidden hover:border-transparent hover:bg-transparent hover:shadow-none',
        // Focus holds the same container hover puts up, so engaging the composer
        // doesn't swap one treatment for another mid-interaction. A to-do's focus
        // is the outline alone, with no fill.
        isFocused &&
          (isTodo
            ? 'border-border hover:border-border shadow-sm hover:shadow-sm'
            : 'border-border bg-background/30 shadow-sm'),
      )}
    >
      <Cell.Header
        showChevron={false}
        className={twMerge(
          showFooterChrome ? 'items-start' : 'items-center',
          '[&_[data-slot=cell-header-content]]:w-full [&_[data-slot=cell-header-content]]:min-w-0 [&_[data-slot=cell-header-content]]:gap-2',
          showFooterChrome
            ? '[&_[data-slot=cell-header-content]]:items-start'
            : '[&_[data-slot=cell-header-content]]:items-center',
        )}
      >
        {isTodo ? (
          // The rows' mark, so the text starts where their titles do. Only drawn: a click
          // anywhere on the row goes to the text.
          <div aria-hidden="true" className="pointer-events-none flex h-5 shrink-0 items-center">
            <TodoMark disabled tabIndex={-1} className={TODO_MARK} />
          </div>
        ) : (
          <div className={twMerge('flex size-5 shrink-0 items-center justify-center', showFooterChrome && 'mt-px')}>
            {/* The one thing that comes up to full strength as the hover container
                arrives — same as the section CTAs (Figma `6972:11353`). */}
            <Plus className="text-foreground-secondary group-hover/cell:text-foreground size-3.5 transition-colors duration-150" />
          </div>
        )}
        <div className="flex w-full min-w-0 flex-1 flex-col gap-2">
          <div ref={containerRef} className="relative w-full min-w-0">
            {/* Stays quiet on hover — it's a placeholder, not a label, and
                bringing it to full strength reads as already-typed text. */}
            {!hasContent && pendingAttachments.length === 0 && (
              <div
                className={twMerge(
                  'text-foreground-placeholder group-hover/cell:text-foreground type-prose-sm pointer-events-none absolute left-0 top-0 font-normal leading-[20px] transition-colors',
                  isTodo && TODO_TITLE_TYPE,
                )}
              >
                {isTodo ? 'New reminder' : 'Jot down a thought, task, idea…'}
              </div>
            )}
            <div
              ref={inputRef}
              contentEditable
              data-reminders-input
              role="textbox"
              aria-label="Add a reminder"
              onFocus={() => {
                requestAnimationFrame(() => {
                  const el = inputRef.current
                  if (!el || !el.textContent) return
                  const range = document.createRange()
                  const sel = window.getSelection()
                  range.selectNodeContents(el)
                  range.collapse(false)
                  sel?.removeAllRanges()
                  sel?.addRange(range)
                })
              }}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              className={twMerge(
                // min-h keeps the empty editable one line tall, so the header
                // centers the pencil icon against the placeholder instead of a
                // collapsed zero-height row.
                'text-foreground selection:bg-element-active [&::-webkit-scrollbar-thumb]:bg-element-active type-prose-sm min-h-[20px] min-w-0 max-w-full whitespace-pre-wrap break-all bg-transparent font-normal leading-[20px] outline-none [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1',
                isTodo && TODO_TITLE_TYPE,
                !isMultiline && 'transition-[max-height] duration-200 ease-out',
              )}
              style={{
                maxHeight: isMultiline ? 200 : 20,
                overflow: isMultiline ? 'auto' : 'hidden',
                marginBottom: isMultiline && showFooterChrome ? ACTIONS_HEIGHT : 0,
              }}
            />
            {showFooterChrome ? (
              <div
                className={twMerge(
                  'absolute left-0 right-0 flex items-center gap-2',
                  isMultiline ? 'bottom-0' : 'top-1/2 -translate-y-1/2',
                )}
              >
                {showTimeSuggestion && storedMatch ? (
                  <p className="text-foreground-secondary type-ui-sm flex items-center gap-1.5">
                    <AnimatedTimeLabel text={formatScheduledAt(new Date(storedMatch.date), hourCycle)} />
                    <Button
                      variant="ghost"
                      shape="square"
                      size="xs"
                      onClick={(e) => {
                        e.stopPropagation()
                        dismissMatch()
                        measureLayout()
                      }}
                      aria-label="Dismiss time suggestion"
                    >
                      <X />
                    </Button>
                  </p>
                ) : null}
                <div className="ml-auto flex items-center gap-1.5">
                  <Tooltip.Provider>
                    <Tooltip>
                      <Tooltip.Trigger
                        render={
                          <Button
                            variant="outline"
                            shape="square"
                            aria-label="Add images"
                            onClick={(e) => {
                              e.stopPropagation()
                              openFilePicker()
                            }}
                          >
                            <Plus />
                          </Button>
                        }
                      />
                      <Tooltip.Content>Add images</Tooltip.Content>
                    </Tooltip>
                    <Popover
                      open={datePickerOpen}
                      onOpenChange={(open) => {
                        setDatePickerOpen(open)
                        if (open) {
                          AmplitudeRenderer.trackEvent('Reminder Action', {
                            type: 'Open Date Picker',
                            source: 'input',
                          })
                        }
                      }}
                    >
                      <Tooltip>
                        {/* A wrapper, not the button: it is disabled while the draft is empty, and a disabled button gets no pointer events. */}
                        <Tooltip.Trigger render={<div className="relative flex" />}>
                          <Popover.Trigger
                            render={
                              <Button
                                variant="outline"
                                shape={manualDate ? undefined : 'square'}
                                className={manualDate ? 'gap-1' : undefined}
                                aria-label="Set date and time"
                                disabled={!hasContent && pendingAttachments.length === 0}
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                          >
                            {manualDate ? (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation()
                                  e.preventDefault()
                                  setDraftScheduledAt(null)
                                  setDatePickerOpen(false)
                                }}
                              >
                                <X />
                              </span>
                            ) : (
                              <Clock />
                            )}
                            {manualDate && (
                              <span className="font-medium leading-none">
                                {manualDate.toDateString() === new Date().toDateString()
                                  ? formatUserTime(manualDate, hourCycle)
                                  : manualDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </Popover.Trigger>
                        </Tooltip.Trigger>
                        <Tooltip.Content>Set date and time</Tooltip.Content>
                      </Tooltip>
                      <Popover.Content
                        side="bottom"
                        align="end"
                        sideOffset={8}
                        initialFocus={false}
                        className="w-[320px] p-1"
                      >
                        <ReminderDatePicker
                          value={manualDate}
                          onChange={(date) => setDraftScheduledAt(date ? date.toISOString() : null)}
                        />
                      </Popover.Content>
                    </Popover>
                  </Tooltip.Provider>
                </div>
              </div>
            ) : null}
          </div>

          {pendingAttachments.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {pendingAttachments.map((a) => (
                <ReminderThumbnail
                  key={a.localId}
                  src={a.previewUrl}
                  uploading={a.uploading}
                  onRemove={() => removePendingAttachment(a.localId)}
                />
              ))}
            </div>
          ) : null}

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={onFileInputChange}
          />
        </div>
      </Cell.Header>
    </Cell>
  )
})
